import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Building2, ChevronDown, Check, Plus, AlertCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const BusinessSelector = ({ className = '' }) => {
  const navigate = useNavigate();
  const { businesses, activeBusiness, switchBusiness, refreshUser } = useAuth();

  const handleSwitchBusiness = async (businessId) => {
    const result = await switchBusiness(businessId);
    if (result) {
      // Refresh to get updated business data
      await refreshUser();
      // Reload the page to fetch new business-specific data
      window.location.reload();
    }
  };

  if (!businesses || businesses.length === 0) {
    return null;
  }

  const currentBusiness = activeBusiness || businesses[0];

  return (
    <div className={`bg-gray-800/50 border border-gray-700 rounded-lg p-4 ${className}`} data-testid="dashboard-business-selector">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <Building2 className="h-6 w-6 text-yellow-500" />
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider">Managing</p>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-white" data-testid="active-business-name">
                {currentBusiness?.business_name}
              </h2>
              <Badge 
                className={currentBusiness?.verification_status === 'verified' 
                  ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                  : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                }
              >
                {currentBusiness?.verification_status === 'verified' ? 'Verified' : 'Pending'}
              </Badge>
            </div>
            {currentBusiness?.contact_email && (
              <p className="text-sm text-gray-500">{currentBusiness.contact_email}</p>
            )}
          </div>
        </div>

        {businesses.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white"
                data-testid="business-switch-dropdown"
              >
                <Building2 className="h-4 w-4 mr-2" />
                Switch Business
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 bg-gray-800 border-gray-700">
              <div className="px-3 py-2 border-b border-gray-700">
                <p className="text-xs text-gray-400 uppercase tracking-wider">
                  Your Businesses ({businesses.length})
                </p>
              </div>
              {businesses.map((biz) => (
                <DropdownMenuItem 
                  key={biz.business_id}
                  onClick={() => handleSwitchBusiness(biz.business_id)}
                  className="text-gray-300 hover:text-white hover:bg-gray-700 cursor-pointer py-3"
                  data-testid={`business-option-${biz.business_id}`}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-yellow-500" />
                      </div>
                      <div>
                        <p className="font-medium">{biz.business_name}</p>
                        <p className="text-xs text-gray-500">
                          {biz.verification_status === 'verified' ? (
                            <span className="text-green-400">Verified</span>
                          ) : biz.verification_status === 'pending' ? (
                            <span className="text-yellow-400">Pending Verification</span>
                          ) : (
                            <span className="text-gray-400">{biz.verification_status}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    {currentBusiness?.business_id === biz.business_id && (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-gray-700" />
              <DropdownMenuItem 
                onClick={() => navigate('/become-vendor')}
                className="text-yellow-500 hover:text-yellow-400 hover:bg-gray-700 cursor-pointer"
                data-testid="add-another-business-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Business
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {businesses.length === 1 && (
          <Button 
            variant="outline" 
            onClick={() => navigate('/become-vendor')}
            className="border-gray-600 text-yellow-500 hover:bg-gray-700 hover:text-yellow-400"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Business
          </Button>
        )}
      </div>

      {/* Email verification warning if needed */}
      {currentBusiness?.email_choice === 'separate' && !currentBusiness?.business_email_verified && (
        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-yellow-400 font-medium">Email Verification Required</p>
            <p className="text-yellow-400/70">
              Your business email needs to be verified. 
              <button 
                onClick={() => navigate(`/business/verify-email?business_id=${currentBusiness.business_id}`)}
                className="underline ml-1 hover:text-yellow-300"
              >
                Verify now
              </button>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessSelector;
